import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  Button,
  Select,
  Dropdown,
  message,
  Drawer,
  Checkbox,
  Spin,
  Collapse,
  Modal,
  Space,
  Switch,
  Progress,
  Tag,
} from "antd";
import {
  SaveOutlined,
  FolderOpenOutlined,
  ThunderboltOutlined,
  PartitionOutlined,
  CloudServerOutlined,
  ApartmentOutlined,
  ColumnWidthOutlined,
  QuestionCircleOutlined,
  ExportOutlined,
  ImportOutlined,
  FileTextOutlined,
  LinkOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { pipelineDiscoveryService } from "../services/pipelineDiscoveryService";
import { accountService } from "../services/accountService";
import { airflowPipelinesService } from "../services/airflowPipelinesService";
import { dashboardApi } from "../services/dashboardApi";
import { API_BASE_URL } from "../services/config";
import fetchWrapper from "../components/utility/fetchWrapper";
import { useLocation, useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import {
  buildPipelineLogsPageParams,
  dedupePipelinesByStableKey,
  getPipelineLogDisplayCounts,
} from "../utils/pipelineDiscoveryHelpers";
import "./WorkflowsPage.css";

/**
 * Workflow Builder — Main purpose
 * This page lets you build a visual dependency graph of your data pipelines (Glue, Step Functions, EMR).
 * You can assign logs and metrics to each pipeline node and see downstream/upstream impact when a
 * pipeline fails — so you know what is affected and what might have caused the failure.
 */
const DRAG_TYPE_PIPELINE = "application/reactflow-pipeline";

const LAYOUT_SPACING = { x: 240, y: 100 };

function getPipelineTypeIcon(type) {
  switch (type) {
    case "glue":
      return <ThunderboltOutlined />;
    case "step_functions":
      return <PartitionOutlined />;
    case "emr":
      return <CloudServerOutlined />;
    case "airflow":
      return <ThunderboltOutlined />;
    default:
      return <ApartmentOutlined />;
  }
}

// Pipeline node on canvas — card style (light bg, icon, clean)
// Each handle has a unique id so edges connect to the handle you actually chose (top/bottom/left/right).
const PipelineNode = ({ data, selected }) => (
  <div
    className={`workflow-pipeline-node ${selected ? "workflow-pipeline-node-selected" : ""}`}
    role="button"
    aria-label={`Pipeline ${data?.pipelineName || data?.label || "node"}`}
    tabIndex={0}
  >
    <Handle type="target" position={Position.Top} id="top" className="workflow-node-handle" />
    <Handle type="source" position={Position.Bottom} id="bottom" className="workflow-node-handle" />
    <Handle type="target" position={Position.Left} id="left" className="workflow-node-handle" />
    <Handle type="source" position={Position.Right} id="right" className="workflow-node-handle" />
    <div className="workflow-pipeline-node__icon-wrap">
      {getPipelineTypeIcon(data?.pipelineType)}
    </div>
    <div className="workflow-pipeline-node__content">
      <div className="workflow-pipeline-node__label">{data?.pipelineName || data?.label || "Pipeline"}</div>
      <div className="workflow-pipeline-node__meta">
        {data?.pipelineType || ""} {data?.region ? ` · ${data?.region}` : ""}
      </div>
      {(data?.selectedLogIds?.length > 0 || data?.selectedMetricIds?.length > 0) && (
        <div className="workflow-pipeline-node__badge">
          {data.selectedLogIds?.length || 0} logs · {data.selectedMetricIds?.length || 0} metrics
        </div>
      )}
    </div>
  </div>
);

const nodeTypes = { pipeline: PipelineNode };

const getId = () => `pipeline_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

// Match backend generate_pipeline_id so pipeline_id is consistent
function generatePipelineId(pipelineType, name, region, resourceId) {
  const pt = pipelineType || "glue";
  const nm = name || "unknown";
  const rg = region || "global";
  if (pt === "emr" && resourceId) {
    const base = `${pt}_${nm}_${resourceId}_${rg}`;
    return base.replace(/ /g, "_").replace(/\//g, "_").replace(/:/g, "_");
  }
  const base = `${pt}_${nm}_${rg}`;
  return base.replace(/ /g, "_").replace(/\//g, "_").replace(/:/g, "_");
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizePipelineType(type) {
  const raw = normalizeText(type).replace(/[\s-]+/g, "_");
  if (!raw) return "";
  if (raw.includes("step")) return "step_functions";
  if (raw.includes("glue")) return "glue";
  if (raw.includes("emr") || raw.includes("elasticmapreduce")) return "emr";
  if (raw.includes("airflow")) return "airflow";
  return raw;
}

function nodeMatchesPipelineHint(node, hint) {
  const data = node?.data || {};
  const nodePipelineId = data.pipelineId || data.pipeline_id || "";
  if (hint.pipelineId && nodePipelineId && nodePipelineId === hint.pipelineId) {
    return true;
  }

  const nodeName = normalizeText(data.pipelineName || data.pipeline_name || data.label);
  const hintName = normalizeText(hint.pipelineName);
  if (!nodeName || !hintName || nodeName !== hintName) return false;

  const nodeType = normalizePipelineType(data.pipelineType || data.pipeline_type || data.type || node?.type);
  const hintType = normalizePipelineType(hint.pipelineType);
  const nodeRegion = normalizeText(data.region || data.pipeline_region);
  const hintRegion = normalizeText(hint.region);

  const typeMatches = !hintType || !nodeType || nodeType === hintType;
  const regionMatches = !hintRegion || !nodeRegion || nodeRegion === hintRegion;
  return typeMatches && regionMatches;
}

function inferPipelineTypeFromRecord(p, fallback = "glue") {
  const explicit = normalizePipelineType(p?.pipeline_type || p?.type);
  if (explicit) return explicit;
  const pid = String(p?.pipeline_id || "").toLowerCase();
  if (pid.startsWith("glue_")) return "glue";
  if (pid.startsWith("step_functions_")) return "step_functions";
  if (pid.startsWith("emr_")) return "emr";
  if (pid.startsWith("airflow_")) return "airflow";
  return fallback;
}

// Match Pipeline Discovery onboarding rules (logs, metrics, or explicit status).
function isPipelineOnboarded(p) {
  const onboardingStatus = String(
    p?.onboarding_status || p?.onboardingStatus || p?.status || "",
  )
    .trim()
    .toLowerCase();
  if (
    onboardingStatus === "full" ||
    onboardingStatus === "partial" ||
    onboardingStatus === "fully_onboarded" ||
    onboardingStatus === "partially_onboarded"
  ) {
    return true;
  }

  const logCounts = getPipelineLogDisplayCounts(p);
  if (logCounts.onboarded > 0) return true;

  if (Number(p?.logs_onboarded ?? 0) > 0) return true;
  if (Number(p?.metrics_onboarded ?? 0) > 0) return true;

  const metrics = Array.isArray(p?.metrics) ? p.metrics : [];
  if (metrics.some((metric) => Boolean(metric?.onboarded))) return true;

  const cw = p.log_paths?.cloudwatch || [];
  const s3 = p.log_paths?.s3 || [];
  return (
    cw.some((x) => typeof x === "object" && x.onboarded) ||
    s3.some((x) => typeof x === "object" && x.onboarded)
  );
}

function toWorkflowPipelineItem(p, fallbackType = "glue") {
  const type = inferPipelineTypeFromRecord(p, p._type || fallbackType);
  const name =
    p.name ||
    p.pipeline_name ||
    p.dag_id ||
    p.jobName ||
    "unknown";
  const region = p.region || p.pipeline_region || "us-east-1";
  const clusterId = type === "emr" ? p.id || p.cluster_id : undefined;

  return {
    pipeline_id:
      p.pipeline_id || generatePipelineId(type, name, region, clusterId),
    pipeline_name: name,
    pipeline_type: type,
    region,
    cluster_id: clusterId,
    environment_name: p.environment_name || p.workspace_name || null,
  };
}

function workflowPipelineKey(item) {
  if (item?.pipeline_type === "emr" && item?.cluster_id) {
    return `emr:${item.cluster_id}`;
  }
  return (
    item?.pipeline_id ||
    `${item?.pipeline_type || ""}:${item?.pipeline_name || ""}:${item?.region || ""}`
  );
}

function dedupeWorkflowPipelineList(items) {
  const map = new Map();
  for (const item of items) {
    const key = workflowPipelineKey(item);
    if (!key || map.has(key)) continue;
    map.set(key, item);
  }

  const typeOrder = { airflow: 0, glue: 1, step_functions: 2, emr: 3 };
  return Array.from(map.values()).sort((a, b) => {
    const typeCompare =
      (typeOrder[a.pipeline_type] ?? 9) - (typeOrder[b.pipeline_type] ?? 9);
    if (typeCompare !== 0) return typeCompare;
    return String(a.pipeline_name || "").localeCompare(String(b.pipeline_name || ""));
  });
}

function buildPipelineListFromDiscovery(discoveryResult) {
  const pipelines = discoveryResult?.pipelines || {};
  const glue = pipelines.glue_jobs || [];
  const step = pipelines.step_functions || [];
  const emr = pipelines.emr_clusters || [];
  const airflow = pipelines.airflow_dags || [];
  const all = [
    ...glue.map((p) => ({ ...p, _type: "glue" })),
    ...step.map((p) => ({ ...p, _type: "step_functions" })),
    ...emr.map((p) => ({ ...p, _type: "emr" })),
    ...airflow.map((p) => ({ ...p, _type: "airflow" })),
  ];
  const deduped = dedupePipelinesByStableKey(
    all.map((p) => ({
      ...p,
      type: inferPipelineTypeFromRecord(p, p._type || "glue"),
    })),
  );

  return deduped
    .filter(isPipelineOnboarded)
    .map((p) => toWorkflowPipelineItem(p, p._type || p.type || "glue"));
}

function buildPipelineListFromOnboardedApi(onboardedPipelines, discoveryResult, seen) {
  return (onboardedPipelines || [])
    .filter((p) => isPipelineOnboarded(p))
    .map((p) => {
      const discP = getPipelineFromDiscovery(discoveryResult, p.pipeline_id);
      if (discP) {
        return toWorkflowPipelineItem(
          {
            ...discP,
            ...p,
            _type: inferPipelineTypeFromRecord(
              discP,
              inferPipelineTypeFromRecord(p),
            ),
          },
          inferPipelineTypeFromRecord(p),
        );
      }
      return toWorkflowPipelineItem(p);
    })
    .filter((item) => !seen.has(workflowPipelineKey(item)));
}

// Find pipeline in discovery by pipeline_id (so we can show logs/metrics from discovery when API returns empty)
function getPipelineFromDiscovery(discoveryResult, pipelineId) {
  if (!discoveryResult?.pipelines || !pipelineId) return null;
  const {
    glue_jobs = [],
    step_functions = [],
    emr_clusters = [],
    airflow_dags = [],
  } = discoveryResult.pipelines;
  for (const p of glue_jobs) {
    if (p.pipeline_id === pipelineId) return p;
    if (generatePipelineId("glue", p.name, p.region) === pipelineId) return p;
  }
  for (const p of step_functions) {
    if (p.pipeline_id === pipelineId) return p;
    if (generatePipelineId("step_functions", p.name, p.region) === pipelineId) return p;
  }
  for (const p of emr_clusters) {
    const rid = p.id || p.cluster_id;
    if (p.pipeline_id === pipelineId) return p;
    if (generatePipelineId("emr", p.name, p.region, rid) === pipelineId) return p;
    if (generatePipelineId("emr", p.name, p.region) === pipelineId) return p;
  }
  for (const p of airflow_dags) {
    const dagName = p.name || p.dag_id || p.pipeline_name;
    if (p.pipeline_id === pipelineId) return p;
    if (generatePipelineId("airflow", dagName, p.region) === pipelineId) return p;
  }
  return null;
}

// Build log list from pipeline discovery (same shape as getLogsByPipeline) for fallback when API returns empty
function logsFromPipelineDiscovery(pipeline) {
  if (!pipeline?.log_paths) return [];
  const cw = Array.isArray(pipeline.log_paths.cloudwatch) ? pipeline.log_paths.cloudwatch : [];
  const s3 = Array.isArray(pipeline.log_paths.s3) ? pipeline.log_paths.s3 : [];
  const items = [];
  for (const path of cw) {
    const pathStr = typeof path === "object" && path.path != null ? path.path : path;
    if (!pathStr) continue;
    items.push({
      log_arn: pathStr,
      log_group_name: pathStr.split(":").slice(-1)[0] || pathStr,
    });
  }
  for (const path of s3) {
    const pathStr = typeof path === "object" && path.path != null ? path.path : path;
    if (!pathStr) continue;
    const after = pathStr.replace(/^s3:\/\//, "");
    const idx = after.indexOf("/");
    const bucket = idx >= 0 ? after.slice(0, idx) : after;
    const prefix = idx >= 0 ? after.slice(idx + 1) : "";
    items.push({ s3_uri: pathStr, bucket_name: bucket, prefix_path: prefix });
  }
  return items;
}

// Build metrics list from pipeline discovery for fallback when API returns empty
function metricsFromPipelineDiscovery(pipeline) {
  return Array.isArray(pipeline?.metrics) ? pipeline.metrics : [];
}

// Flatten metrics so we show one option per metric name (not per service). API returns service-level
// items with a nested metrics[] array; discovery returns a flat list of { name, namespace, ... }.
function flattenMetricsForDisplay(metricsList) {
  if (!Array.isArray(metricsList)) return [];
  const out = [];
  for (const m of metricsList) {
    const metricsArray = m.metrics;
    if (Array.isArray(metricsArray) && metricsArray.length > 0) {
      for (const sub of metricsArray) {
        out.push({
          ...m,
          name: sub.name ?? m.name,
          _metricName: sub.name,
          _statistics: sub.statistics,
        });
      }
    } else {
      out.push({ ...m, _metricName: m.name });
    }
  }
  return out;
}

/** Convert CloudWatch namespace/metric/stat to YACE-style Prometheus metric name pattern */
function toPrometheusMetricPattern(namespace, metricName, stat) {
  const ns = (namespace || "").replace(/[/.-]/g, "_").toLowerCase();
  const mn = (metricName || "").replace(/[/.-]/g, "_").toLowerCase();
  const st = (stat || "sum").toLowerCase();
  return `aws_cloudwatch_${ns}_${mn}_${st}`;
}

/** Build PromQL for a metric config. Returns null if config is insufficient. */
function buildPromQLForMetric(metric, awsAccountId) {
  if (metric.promql || metric.prometheus_query) return metric.promql || metric.prometheus_query;
  const ns = metric.namespace;
  const name = metric._metricName ?? metric.name;
  if (!ns || !name) return null;
  const stats = metric._statistics || metric.statistics || ["Sum", "Average"];
  const stat = Array.isArray(stats) ? stats[0] : stats;
  const metricPattern = toPrometheusMetricPattern(ns, name, stat);
  const dims = metric.dimensions || {};
  const selectors = [`__name__=~"${metricPattern}"`];
  if (awsAccountId) selectors.push(`account_id="${awsAccountId}"`);
  for (const [k, v] of Object.entries(dims)) {
    if (v) selectors.push(`dimension_${k}="${String(v).replace(/"/g, '\\"')}"`);
  }
  return `sum({${selectors.join(", ")}})`;
}

/** Extract latest value from Prometheus range result */
function extractLatestValue(result) {
  if (!result?.result || !Array.isArray(result.result)) return null;
  const series = result.result[0];
  if (!series?.values?.length) return null;
  const last = series.values[series.values.length - 1];
  return last && last[1] != null ? parseFloat(last[1]) : null;
}

/** Auto-layout: order nodes by graph flow (sources first), then arrange vertically or horizontally */
function applyAutoLayout(nodes, edges, direction) {
  const idToNode = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const inDegree = {};
  nodes.forEach((n) => { inDegree[n.id] = 0; });
  edges.forEach((e) => { inDegree[e.target] = (inDegree[e.target] || 0) + 1; });
  const queue = nodes.filter((n) => inDegree[n.id] === 0).map((n) => n.id);
  const order = [];
  while (queue.length) {
    const id = queue.shift();
    order.push(id);
    edges.filter((e) => e.source === id).forEach((e) => {
      inDegree[e.target] = (inDegree[e.target] || 0) - 1;
      if (inDegree[e.target] === 0) queue.push(e.target);
    });
  }
  nodes.forEach((n) => { if (!order.includes(n.id)) order.push(n.id); });
  const spacing = LAYOUT_SPACING;
  return nodes.map((n) => {
    const i = order.indexOf(n.id);
    const position = direction === "horizontal"
      ? { x: i * spacing.x, y: 0 }
      : { x: 0, y: i * spacing.y };
    return { ...n, position };
  });
}

function formatPipelineTypeLabel(type) {
  const t = String(type || "").toLowerCase();
  if (t === "glue") return "Glue job";
  if (t === "step_functions") return "Step Functions";
  if (t === "emr") return "EMR";
  if (t === "airflow") return "Airflow";
  return type || "Pipeline";
}

const WorkflowsPage = () => {
  const { user, accounts, setAccounts } = useAuth();
  const { isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [pipelineList, setPipelineList] = useState([]);
  const [pipelinesLoading, setPipelinesLoading] = useState(false);
  const [discoveryResult, setDiscoveryResult] = useState(null);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [configDrawerOpen, setConfigDrawerOpen] = useState(false);
  const [logsForPipeline, setLogsForPipeline] = useState([]);
  const [metricsForPipeline, setMetricsForPipeline] = useState([]);
  const [logsMetricsLoading, setLogsMetricsLoading] = useState(false);
  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [selectedMetricIds, setSelectedMetricIds] = useState([]);
  const [lineageEnabled, setLineageEnabled] = useState(false);
  const [lineageLoading, setLineageLoading] = useState(false);

  // Phase 2: Airflow DAG detail, metrics, runs
  const [airflowEnvironments, setAirflowEnvironments] = useState([]);
  const [selectedAirflowEnvironment, setSelectedAirflowEnvironment] = useState(null);
  const [dagDetail, setDagDetail] = useState(null);
  const [dagMetrics, setDagMetrics] = useState(null);
  const [dagRuns, setDagRuns] = useState(null);
  const [dagPhase2Loading, setDagPhase2Loading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);

  // Config-driven metrics: values from Prometheus per metric config
  const [configMetricValues, setConfigMetricValues] = useState({});
  const [configMetricsLoading, setConfigMetricsLoading] = useState(false);

  const [saveLoading, setSaveLoading] = useState(false);
  const [workflowName, setWorkflowName] = useState("My Workflow");
  const [diagramList, setDiagramList] = useState([]);
  const [loadMenuOpen, setLoadMenuOpen] = useState(false);
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);
  const deepLinkAppliedRef = useRef(false);

  const deepLink = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const rawAccountId = q.get("accountId") || "";
    return {
      accountId: rawAccountId.startsWith("aws_")
        ? rawAccountId.slice(4)
        : rawAccountId,
      clientId: q.get("clientId") || "",
      pipelineId: q.get("pipelineId") || "",
      pipelineName: q.get("pipeline") || "",
      pipelineType: q.get("pipelineType") || "",
      region: q.get("region") || "",
      editLineage: q.get("editLineage") === "1",
    };
  }, [location.search]);

  const clientId = selectedAccount?.clientId || user?.client_id;
  const awsAccountId = selectedAccount?.accountId;

  const selectedAirflowEnvironmentRegion = useMemo(() => {
    const env = (airflowEnvironments || []).find((e) => e.name === selectedAirflowEnvironment);
    return env?.region || null;
  }, [airflowEnvironments, selectedAirflowEnvironment]);

  const awsAccounts = useMemo(() => {
    return (accounts || []).filter((acc) => acc.accountId && acc.status === "completed");
  }, [accounts]);

  const isAirflowNode = selectedNode?.data?.pipelineType === "airflow";
  const dagId = isAirflowNode ? (selectedNode?.data?.pipelineName || selectedNode?.data?.label) : null;

  // Fetch MWAA environments when account selected
  useEffect(() => {
    if (!selectedAccount?.accountId) {
      setAirflowEnvironments([]);
      setSelectedAirflowEnvironment(null);
      return;
    }
    airflowPipelinesService
      .getEnvironments(selectedAccount.accountId, clientId)
      .then((res) => {
        setAirflowEnvironments(res.environments || []);
        if (!selectedAirflowEnvironment && (res.environments || []).length > 0) {
          setSelectedAirflowEnvironment(res.environments[0].name);
        }
      })
      .catch(() => setAirflowEnvironments([]));
  }, [selectedAccount?.accountId, clientId]);

  // Fetch Phase 2 data when Airflow node selected and environment available
  useEffect(() => {
    if (!isAirflowNode || !dagId || !selectedAirflowEnvironment) {
      setDagDetail(null);
      setDagMetrics(null);
      setDagRuns(null);
      return;
    }
    setDagPhase2Loading(true);
    setDagDetail(null);
    setDagMetrics(null);
    setDagRuns(null);
    Promise.all([
      airflowPipelinesService.getDagDetail(
        dagId,
        selectedAirflowEnvironment,
        awsAccountId,
        selectedAirflowEnvironmentRegion,
        clientId
      ),
      airflowPipelinesService.getDagMetrics(
        dagId,
        selectedAirflowEnvironment,
        7,
        awsAccountId,
        selectedAirflowEnvironmentRegion,
        clientId
      ),
      airflowPipelinesService.getDagRuns(dagId, selectedAirflowEnvironment, {
        limit: 20,
        offset: 0,
        awsAccountId,
        environmentRegion: selectedAirflowEnvironmentRegion,
        clientId,
      }),
    ])
      .then(([detail, metrics, runs]) => {
        setDagDetail(detail);
        setDagMetrics(metrics);
        setDagRuns(runs);
      })
      .catch((err) => {
        console.error("[WorkflowsPage] Phase 2 fetch failed:", err);
        message.error(err.message || "Failed to load DAG details");
      })
      .finally(() => setDagPhase2Loading(false));
  }, [
    isAirflowNode,
    dagId,
    selectedAirflowEnvironment,
    selectedAirflowEnvironmentRegion,
    awsAccountId,
    clientId,
  ]);

  // Fetch config-driven metric values from Prometheus when metricsForPipeline changes
  useEffect(() => {
    const flat = flattenMetricsForDisplay(metricsForPipeline);
    if (flat.length === 0) {
      setConfigMetricValues({});
      return;
    }
    let cancelled = false;
    setConfigMetricsLoading(true);
    setConfigMetricValues({});
    const now = Math.floor(Date.now() / 1000);
    const start = now - 3600; // last 1h
    const end = now;
    const step = "60s";
    Promise.all(
      flat.map(async (m, i) => {
        const key = m.SK || m.metric_config_id || `${m.namespace}#${m._metricName ?? m.name}` || `m${i}`;
        const promql = buildPromQLForMetric(m, awsAccountId);
        if (!promql) return { key, value: null };
        try {
          const res = await dashboardApi.executeQuery({
            datasourceId: "default",
            query: promql,
            start,
            end,
            step,
          });
          if (cancelled) return { key, value: null };
          const val = res?.success && res?.data ? extractLatestValue(res.data) : null;
          return { key, value: val };
        } catch {
          return { key, value: null };
        }
      })
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      results.forEach(({ key, value }) => { map[key] = value; });
      setConfigMetricValues(map);
    }).finally(() => {
      if (!cancelled) setConfigMetricsLoading(false);
    });
    return () => { cancelled = true; };
  }, [metricsForPipeline, awsAccountId]);

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!user?.client_id) return;
      if ((accounts || []).length === 0 && !accountsLoading) {
        setAccountsLoading(true);
        try {
          const allAccounts = await accountService.getAccounts();
          const mapped = [];
          for (const cid in allAccounts) {
            for (const aid in allAccounts[cid]) {
              mapped.push({ ...allAccounts[cid][aid], accountId: aid, clientId: cid });
            }
          }
          if (setAccounts) setAccounts(mapped);
        } catch (e) {
          console.error(e);
          message.error("Failed to load AWS accounts");
        } finally {
          setAccountsLoading(false);
        }
      }
    };
    fetchAccounts();
  }, [user?.client_id, accounts?.length, accountsLoading, setAccounts]);

  useEffect(() => {
    deepLinkAppliedRef.current = false;
  }, [location.search]);

  useEffect(() => {
    if (selectedAccount || !deepLink.accountId || awsAccounts.length === 0) return;
    const match = awsAccounts.find(
      (a) =>
        a.accountId === deepLink.accountId &&
        (!deepLink.clientId || a.clientId === deepLink.clientId)
    );
    if (match) setSelectedAccount(match);
  }, [deepLink.accountId, deepLink.clientId, awsAccounts, selectedAccount]);

  // Load pipelines: merge discovery (Glue, Step Functions, EMR) + onboarded (includes Airflow DAGs)
  useEffect(() => {
    if (!clientId || !awsAccountId) {
      setPipelineList([]);
      return;
    }
    setPipelinesLoading(true);
    Promise.all([
      pipelineDiscoveryService.discoverPipelines(clientId, awsAccountId, { autoOnboard: false }),
      pipelineDiscoveryService.getOnboardedPipelines(clientId, awsAccountId),
    ])
      .then(([discoveryRes, onboardedRes]) => {
        setDiscoveryResult(discoveryRes);
        const fromDiscovery = buildPipelineListFromDiscovery(discoveryRes);
        const seen = new Set(fromDiscovery.map((p) => workflowPipelineKey(p)));
        const fromOnboarded = buildPipelineListFromOnboardedApi(
          onboardedRes?.pipelines || [],
          discoveryRes,
          seen,
        );
        setPipelineList(dedupeWorkflowPipelineList([...fromDiscovery, ...fromOnboarded]));
      })
      .catch(() => {
        pipelineDiscoveryService
          .getOnboardedPipelines(clientId, awsAccountId)
          .then((res) =>
            setPipelineList(
              dedupeWorkflowPipelineList(
                (res.pipelines || [])
                  .filter(isPipelineOnboarded)
                  .map((p) => toWorkflowPipelineItem(p)),
              ),
            ),
          )
          .catch(() => {
            message.error("Failed to load pipelines");
            setPipelineList([]);
          });
      })
      .finally(() => setPipelinesLoading(false));
  }, [clientId, awsAccountId]);

  const tryLoadExistingDiagramForDeepLink = useCallback(
    async (hint) => {
      if (!clientId) return { loaded: false, name: null };
      const hasHint = Boolean(hint?.pipelineId || hint?.pipelineName);
      if (!hasHint) return { loaded: false, name: null };

      try {
        const listResp = await fetchWrapper(
          `${API_BASE_URL}/api/v1/architecture/list/${encodeURIComponent(clientId)}`
        );
        const listData = await listResp.json();
        if (!listResp.ok) return { loaded: false, name: null };

        const names = (listData?.diagrams || [])
          .map((d) => (typeof d === "string" ? d : d?.name))
          .filter(Boolean);

        for (const name of names) {
          const loadResp = await fetchWrapper(
            `${API_BASE_URL}/api/v1/architecture/load/${encodeURIComponent(clientId)}?name=${encodeURIComponent(name)}`
          );
          const loadData = await loadResp.json();
          if (!loadResp.ok) continue;

          const diagram = loadData?.diagram;
          if (!diagram?.nodes?.length) continue;
          const matched = diagram.nodes.some((n) => nodeMatchesPipelineHint(n, hint));
          if (!matched) continue;

          setNodes(diagram.nodes);
          setEdges(diagram.edges || []);
          setWorkflowName(name);
          return { loaded: true, name };
        }
      } catch (error) {
        console.error("[WorkflowsPage] Failed to auto-load deep-linked lineage", error);
      }

      return { loaded: false, name: null };
    },
    [clientId, setNodes, setEdges]
  );

  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    if (!selectedAccount || pipelinesLoading) return;
    if (deepLink.accountId && selectedAccount.accountId !== deepLink.accountId)
      return;

    const hasPipelineHint = Boolean(deepLink.pipelineId || deepLink.pipelineName);
    if (!hasPipelineHint) return;
    deepLinkAppliedRef.current = true;

    let cancelled = false;

    const applyDeepLink = async () => {
      if (deepLink.editLineage) {
        const loaded = await tryLoadExistingDiagramForDeepLink(deepLink);
        if (cancelled) return;
        if (loaded.loaded) {
          message.info(`Loaded "${loaded.name}" for lineage edit. Remove links and click Save to persist.`);
          return;
        }
      }

      let target = pipelineList.find(
        (p) =>
          (deepLink.pipelineId && p.pipeline_id === deepLink.pipelineId) ||
          (deepLink.pipelineName && p.pipeline_name === deepLink.pipelineName)
      );
      if (!target && deepLink.pipelineName) {
        target = pipelineList.find(
          (p) =>
            String(p.pipeline_name || "").toLowerCase() ===
            deepLink.pipelineName.toLowerCase()
        );
      }

      const derived = target || {
        pipeline_id:
          deepLink.pipelineId ||
          generatePipelineId(
            deepLink.pipelineType || "glue",
            deepLink.pipelineName || "pipeline",
            deepLink.region || "us-east-1"
          ),
        pipeline_name: deepLink.pipelineName || "Pipeline",
        pipeline_type: deepLink.pipelineType || "glue",
        region: deepLink.region || "us-east-1",
      };

      setNodes((nds) => {
        const exists = nds.some(
          (n) =>
            (derived.pipeline_id && n.data?.pipelineId === derived.pipeline_id) ||
            n.data?.pipelineName === derived.pipeline_name
        );
        if (exists) return nds;
        const position = {
          x: 220 + (nds.length % 3) * LAYOUT_SPACING.x,
          y: 120 + Math.floor(nds.length / 3) * LAYOUT_SPACING.y,
        };
        return nds.concat({
          id: getId(),
          type: "pipeline",
          position,
          data: {
            pipelineId: derived.pipeline_id,
            pipelineName: derived.pipeline_name,
            pipelineType: derived.pipeline_type,
            region: derived.region,
            label: derived.pipeline_name,
            selectedLogIds: [],
            selectedMetricIds: [],
          },
        });
      });

      if (deepLink.editLineage) {
        message.info("No saved lineage found for this pipeline. Build or modify links and click Save.");
      }
    };

    applyDeepLink();
    return () => {
      cancelled = true;
    };
  }, [deepLink, selectedAccount, pipelinesLoading, pipelineList, setNodes, tryLoadExistingDiagramForDeepLink]);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const raw = event.dataTransfer.getData(DRAG_TYPE_PIPELINE);
      if (!raw || !reactFlowInstance) return;
      try {
        const payload = JSON.parse(raw);
        const position = reactFlowInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY });
        const newNode = {
          id: getId(),
          type: "pipeline",
          position,
          data: {
            pipelineId: payload.pipeline_id,
            pipelineName: payload.pipeline_name,
            pipelineType: payload.pipeline_type,
            region: payload.region,
            label: payload.pipeline_name,
            selectedLogIds: payload.selectedLogIds || [],
            selectedMetricIds: payload.selectedMetricIds || [],
          },
        };
        setNodes((nds) => nds.concat(newNode));
      } catch (e) {
        console.error(e);
      }
    },
    [reactFlowInstance, setNodes]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed } }, eds)),
    [setEdges]
  );

  const onEdgeClick = useCallback((event, edge) => {
    setSelectedEdgeId(edge.id);
    setSelectedNode(null);
    setConfigDrawerOpen(true);
  }, []);

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setSelectedEdgeId(null);
    setConfigDrawerOpen(true);
    setSelectedLogIds(node.data?.selectedLogIds || []);
    setSelectedMetricIds(node.data?.selectedMetricIds || []);

    const pid = node.data?.pipelineId;
    if (!pid || !clientId || !awsAccountId) {
      setLogsForPipeline([]);
      setMetricsForPipeline([]);
      return;
    }
    setLogsMetricsLoading(true);
    setLineageEnabled(false);
    Promise.all([
      pipelineDiscoveryService.getLogsByPipeline(pid, { clientId, awsAccountId }),
      pipelineDiscoveryService.getMetricsByPipeline(pid, { clientId, awsAccountId }),
      clientId && awsAccountId
        ? pipelineDiscoveryService.getPipelineLineage(pid, clientId, awsAccountId).catch(() => ({ lineage: { enabled: false } }))
        : Promise.resolve({ lineage: { enabled: false } }),
    ])
      .then(([logRes, metricRes, lineageRes]) => {
        let logs = logRes?.logs || [];
        let metrics = metricRes?.metrics || [];
        // If DynamoDB has no logs/metrics for this pipeline (e.g. onboarded before pipeline_id was set),
        // show logs and metrics from discovery so Workflows matches Pipeline Discovery.
        if ((logs.length === 0 || metrics.length === 0) && discoveryResult) {
          const pipeline = getPipelineFromDiscovery(discoveryResult, pid);
          if (pipeline) {
            if (logs.length === 0) logs = logsFromPipelineDiscovery(pipeline);
            if (metrics.length === 0) metrics = metricsFromPipelineDiscovery(pipeline);
          }
        }
        setLogsForPipeline(logs);
        setMetricsForPipeline(metrics);
        setLineageEnabled(Boolean(lineageRes?.lineage?.enabled));
      })
      .catch(() => {
        setLogsForPipeline([]);
        setMetricsForPipeline([]);
      })
      .finally(() => setLogsMetricsLoading(false));
  }, [clientId, awsAccountId, discoveryResult]);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdgeId(null);
    setConfigDrawerOpen(false);
  }, []);

  const applyLogsMetricsToNode = useCallback(() => {
    if (!selectedNode) return;
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== selectedNode.id) return n;
        return {
          ...n,
          data: {
            ...n.data,
            selectedLogIds: [...selectedLogIds],
            selectedMetricIds: [...selectedMetricIds],
          },
        };
      })
    );
    message.success("Logs & metrics updated for this pipeline");
  }, [selectedNode, selectedLogIds, selectedMetricIds, setNodes]);

  const handleViewLogs = useCallback(() => {
    const pipelineId = selectedNode?.data?.pipelineId;
    const name = selectedNode?.data?.pipelineName || selectedNode?.data?.label || "";
    const pipeline =
      (pipelineId && discoveryResult
        ? getPipelineFromDiscovery(discoveryResult, pipelineId)
        : null) || (name ? { name, pipeline_name: name } : null);
    if (!pipeline) return;
    const q = buildPipelineLogsPageParams(pipeline, awsAccountId);
    navigate(`/logs?${q.toString()}`);
    setConfigDrawerOpen(false);
    setSelectedNode(null);
  }, [selectedNode, discoveryResult, awsAccountId, navigate]);

  const handleLineageToggle = useCallback(
    async (enabled) => {
      const pid = selectedNode?.data?.pipelineId;
      if (!pid || !clientId || !awsAccountId) return;
      setLineageLoading(true);
      try {
        await pipelineDiscoveryService.upsertPipelineLineage({
          clientId,
          awsAccountId,
          pipelineId: pid,
          enabled,
          pipelineName: selectedNode?.data?.pipelineName,
          pipelineType: selectedNode?.data?.pipelineType,
          pipelineRegion: selectedNode?.data?.region,
        });
        setLineageEnabled(enabled);
        message.success(enabled ? "Lineage enabled" : "Lineage disabled");
      } catch (e) {
        message.error(e.message || "Failed to update lineage");
      } finally {
        setLineageLoading(false);
      }
    },
    [selectedNode, clientId, awsAccountId]
  );

  const handleTriggerRun = useCallback(async () => {
    if (!dagId || !selectedAirflowEnvironment) return;
    setTriggerLoading(true);
    try {
      await airflowPipelinesService.triggerDagRun(dagId, selectedAirflowEnvironment, {
        awsAccountId,
        environmentRegion: selectedAirflowEnvironmentRegion,
        clientId,
      });
      message.success("DAG run triggered successfully");
      // Refresh Phase 2 data
      const [detail, metrics, runs] = await Promise.all([
        airflowPipelinesService.getDagDetail(
          dagId,
          selectedAirflowEnvironment,
          awsAccountId,
          selectedAirflowEnvironmentRegion,
          clientId
        ),
        airflowPipelinesService.getDagMetrics(
          dagId,
          selectedAirflowEnvironment,
          7,
          awsAccountId,
          selectedAirflowEnvironmentRegion,
          clientId
        ),
        airflowPipelinesService.getDagRuns(dagId, selectedAirflowEnvironment, {
          limit: 20,
          offset: 0,
          awsAccountId,
          environmentRegion: selectedAirflowEnvironmentRegion,
          clientId,
        }),
      ]);
      setDagDetail(detail);
      setDagMetrics(metrics);
      setDagRuns(runs);
    } catch (e) {
      message.error(e.message || "Failed to trigger DAG run");
    } finally {
      setTriggerLoading(false);
    }
  }, [
    dagId,
    selectedAirflowEnvironment,
    selectedAirflowEnvironmentRegion,
    awsAccountId,
    clientId,
  ]);

  const formatDuration = (seconds) => {
    if (seconds == null || seconds < 0) return "—";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  const downstreamIds = useMemo(() => {
    if (!selectedNode) return [];
    const out = new Set();
    const stack = [selectedNode.id];
    while (stack.length) {
      const cur = stack.pop();
      edges.filter((e) => e.source === cur).forEach((e) => {
        if (!out.has(e.target)) {
          out.add(e.target);
          stack.push(e.target);
        }
      });
    }
    return Array.from(out);
  }, [selectedNode, edges]);

  const upstreamIds = useMemo(() => {
    if (!selectedNode) return [];
    const out = new Set();
    const stack = [selectedNode.id];
    while (stack.length) {
      const cur = stack.pop();
      edges.filter((e) => e.target === cur).forEach((e) => {
        if (!out.has(e.source)) {
          out.add(e.source);
          stack.push(e.source);
        }
      });
    }
    return Array.from(out);
  }, [selectedNode, edges]);

  const handleSave = useCallback(async () => {
    if (!clientId) {
      message.error("Select an account and ensure you are logged in.");
      return;
    }
    setSaveLoading(true);
    try {
      const response = await fetchWrapper(`${API_BASE_URL}/api/v1/architecture/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: clientId,
          name: workflowName,
          nodes: nodes.map((n) => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
          edges: edges.map((e) => ({
            id: e.id,
            source: e.source,
            target: e.target,
            ...(e.sourceHandle && { sourceHandle: e.sourceHandle }),
            ...(e.targetHandle && { targetHandle: e.targetHandle }),
          })),
        }),
      });
      const data = await response.json();
      if (response.ok) {
        message.success("Workflow saved successfully");
      } else {
        throw new Error(data.detail || "Failed to save");
      }
    } catch (e) {
      message.error(e.message || "Failed to save workflow");
    } finally {
      setSaveLoading(false);
    }
  }, [clientId, workflowName, nodes, edges]);

  const handleLoadList = useCallback(async () => {
    if (!clientId) return;
    try {
      const response = await fetchWrapper(`${API_BASE_URL}/api/v1/architecture/list/${encodeURIComponent(clientId)}`);
      const data = await response.json();
      if (response.ok && data.diagrams) {
        setDiagramList(data.diagrams);
      }
    } catch (e) {
      message.error("Failed to load workflow list");
    }
  }, [clientId]);

  const handleLoadDiagram = useCallback(
    async (name) => {
      if (!clientId) return;
      try {
        const response = await fetchWrapper(
          `${API_BASE_URL}/api/v1/architecture/load/${encodeURIComponent(clientId)}?name=${encodeURIComponent(name)}`
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || "Failed to load");
        const diagram = data.diagram;
        if (diagram?.nodes?.length) {
          setNodes(diagram.nodes);
          setEdges(diagram.edges || []);
          setWorkflowName(name);
          message.success(`Loaded "${name}"`);
        }
        setLoadMenuOpen(false);
      } catch (e) {
        message.error(e.message || "Failed to load workflow");
      }
    },
    [clientId, setNodes, setEdges]
  );

  // Keyboard: Escape closes config drawer; ? opens shortcuts
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (configDrawerOpen) {
          setConfigDrawerOpen(false);
          setSelectedNode(null);
        }
      }
      if (e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        setShortcutsModalOpen((open) => !open);
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [configDrawerOpen]);

  const closeDrawer = useCallback(() => {
    setConfigDrawerOpen(false);
    setSelectedNode(null);
    setSelectedEdgeId(null);
  }, []);

  const updateEdgeHandles = useCallback(
    (sourceHandle, targetHandle) => {
      if (!selectedEdgeId) return;
      setEdges((eds) =>
        eds.map((e) =>
          e.id === selectedEdgeId
            ? { ...e, sourceHandle: sourceHandle ?? e.sourceHandle, targetHandle: targetHandle ?? e.targetHandle }
            : e
        )
      );
    },
    [selectedEdgeId, setEdges]
  );

  const handleAutoLayout = useCallback((direction) => {
    setNodes((nds) => applyAutoLayout(nds, edges, direction));
    message.success(`Layout: ${direction === "horizontal" ? "Horizontal" : "Vertical"}`);
  }, [edges, setNodes]);

  return (
    <div
      className={`workflows-page ${isDark ? "workflows-page--dark" : ""}`}
      role="main"
      aria-label="Workflow builder"
    >
      <header className="workflows-toolbar" role="toolbar" aria-label="Workflow actions">
        <div className="workflows-toolbar__title">
          <h1 className="workflows-toolbar__title-text">Workflow builder</h1>
        </div>
        <div className="workflows-toolbar__actions">
          <Select
            placeholder="AWS account"
            value={selectedAccount ? `${selectedAccount.clientId}|${selectedAccount.accountId}` : undefined}
            onChange={(val) => {
              if (!val) {
                setSelectedAccount(null);
                return;
              }
              const [cid, aid] = val.split("|");
              setSelectedAccount(awsAccounts.find((a) => a.clientId === cid && a.accountId === aid) || null);
            }}
            style={{ minWidth: 200 }}
            allowClear
            loading={accountsLoading}
            options={awsAccounts.map((a) => ({
              value: `${a.clientId}|${a.accountId}`,
              label: `${a.accountId}${a.accountName ? ` · ${a.accountName}` : ""}`,
            }))}
            aria-label="Select AWS account"
          />
          {airflowEnvironments.length > 0 && (
            <Select
              placeholder="MWAA environment"
              value={selectedAirflowEnvironment || undefined}
              onChange={setSelectedAirflowEnvironment}
              style={{ minWidth: 180 }}
              allowClear
              options={airflowEnvironments.map((e) => ({
                value: e.name,
                label: `${e.name}${e.status ? ` (${e.status})` : ""}`,
              }))}
              aria-label="Select MWAA environment"
            />
          )}
          <Dropdown
            menu={{
              items: [
                { key: "v", label: "Vertical", onClick: () => handleAutoLayout("vertical") },
                { key: "h", label: "Horizontal", onClick: () => handleAutoLayout("horizontal") },
              ],
            }}
            placement="bottomRight"
          >
            <Button icon={<ColumnWidthOutlined />} aria-label="Auto-layout">
              Layout
            </Button>
          </Dropdown>
          <Dropdown
            open={loadMenuOpen}
            onOpenChange={(open) => {
              setLoadMenuOpen(open);
              if (open) handleLoadList();
            }}
            menu={{
              items: diagramList.length
                ? diagramList.map((d) => ({
                    key: d.name || d,
                    label: d.name || d,
                    onClick: () => handleLoadDiagram(d.name || d),
                  }))
                : [{ key: "none", label: "No saved workflows", disabled: true }],
            }}
            placement="bottomRight"
          >
            <Button icon={<FolderOpenOutlined />} aria-label="Load workflow">
              Load
            </Button>
          </Dropdown>
          <Button type="primary" icon={<SaveOutlined />} loading={saveLoading} onClick={handleSave} aria-label="Save workflow">
            Save
          </Button>
          <Button icon={<QuestionCircleOutlined />} onClick={() => setShortcutsModalOpen(true)} aria-label="Keyboard shortcuts" />
        </div>
      </header>

      <Modal
        title="Keyboard shortcuts"
        open={shortcutsModalOpen}
        onCancel={() => setShortcutsModalOpen(false)}
        footer={null}
        width={360}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <div><kbd>Escape</kbd> Close properties panel</div>
          <div><kbd>Delete</kbd> / <kbd>Backspace</kbd> Delete selected node or edge</div>
          <div><kbd>?</kbd> Show this help</div>
        </Space>
      </Modal>

      <div className="workflows-main">
        <aside className="workflows-sidebar" aria-label="Nodes library">
          <div className="workflows-sidebar__header">
            <h2 className="workflows-sidebar__title">Nodes Library</h2>
          </div>
          <div className="workflows-sidebar__list" role="list">
            {!selectedAccount ? (
              <div className="workflows-empty">
                <p className="workflows-empty__text">Select an AWS account</p>
              </div>
            ) : pipelinesLoading ? (
              <div className="workflows-empty">
                <Spin size="small" />
              </div>
            ) : pipelineList.length === 0 ? (
              <div className="workflows-empty">
                <p className="workflows-empty__text">No onboarded pipelines. Use Pipeline Discovery first.</p>
              </div>
            ) : (
              pipelineList.map((p) => (
                <div
                  key={p.pipeline_id}
                  className="workflows-pipeline-item"
                  role="listitem"
                  tabIndex={0}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData(
                      DRAG_TYPE_PIPELINE,
                      JSON.stringify({
                        pipeline_id: p.pipeline_id,
                        pipeline_name: p.pipeline_name,
                        pipeline_type: p.pipeline_type,
                        region: p.region,
                      })
                    );
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.currentTarget.focus();
                    }
                  }}
                >
                  <div className="workflows-pipeline-item__icon">
                    {getPipelineTypeIcon(p.pipeline_type)}
                  </div>
                  <div className="workflows-pipeline-item__body">
                    <span className="workflows-pipeline-item__name">{p.pipeline_name}</span>
                    <span className="workflows-pipeline-item__meta">
                      {formatPipelineTypeLabel(p.pipeline_type)} · {p.region || "—"}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </aside>

        <div className="workflows-canvas-wrap">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onEdgeClick={onEdgeClick}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            fitView
            snapToGrid
            snapGrid={[15, 15]}
            defaultEdgeOptions={{
              type: "smoothstep",
              markerEnd: { type: MarkerType.ArrowClosed },
              style: { stroke: "#94a3b8", strokeWidth: 2 },
            }}
            deleteKeyCode={["Backspace", "Delete"]}
          >
            <Background color="var(--wf-grid-dot)" gap={16} size={1} />
            <Controls />
            <MiniMap />
          </ReactFlow>
        </div>
      </div>

      <Drawer
        className="workflows-drawer workflows-drawer--enhanced"
        title={
          selectedEdgeId ? (
            "Edit connection"
          ) : selectedNode ? (
            <div className="workflows-drawer-header">
              <span className="workflows-drawer-header__title">{selectedNode.data?.pipelineName || selectedNode.data?.label || "Pipeline"}</span>
              <div className="workflows-drawer-header__meta">
                <Tag color="blue">{formatPipelineTypeLabel(selectedNode.data?.pipelineType)}</Tag>
                {selectedNode.data?.region && <Tag>{selectedNode.data.region}</Tag>}
              </div>
            </div>
          ) : (
            "Properties"
          )
        }
        open={configDrawerOpen}
        onClose={closeDrawer}
        width={400}
        closable
        destroyOnClose
        keyboard
        bodyStyle={{ paddingBottom: 24 }}
      >
        {selectedEdgeId && (() => {
          const edge = edges.find((e) => e.id === selectedEdgeId);
          const sourceNode = nodes.find((n) => n.id === edge?.source);
          const targetNode = nodes.find((n) => n.id === edge?.target);
          const sourceHandleOptions = [
            { value: "bottom", label: "Bottom" },
            { value: "right", label: "Right" },
          ];
          const targetHandleOptions = [
            { value: "top", label: "Top" },
            { value: "left", label: "Left" },
          ];
          return (
            <div className="workflows-drawer-section">
              <p className="workflows-drawer-section__title" style={{ marginBottom: 8 }}>
                Change which points the line connects
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <label className="workflows-drawer-section__title" style={{ display: "block", marginBottom: 4 }}>
                    From ({sourceNode?.data?.pipelineName || edge?.source})
                  </label>
                  <Select
                    value={edge?.sourceHandle ?? "bottom"}
                    onChange={(v) => updateEdgeHandles(v, undefined)}
                    options={sourceHandleOptions}
                    style={{ width: "100%" }}
                    aria-label="Source handle"
                  />
                </div>
                <div>
                  <label className="workflows-drawer-section__title" style={{ display: "block", marginBottom: 4 }}>
                    To ({targetNode?.data?.pipelineName || edge?.target})
                  </label>
                  <Select
                    value={edge?.targetHandle ?? "top"}
                    onChange={(v) => updateEdgeHandles(undefined, v)}
                    options={targetHandleOptions}
                    style={{ width: "100%" }}
                    aria-label="Target handle"
                  />
                </div>
              </div>
            </div>
          );
        })()}
        {selectedNode && (
          <div className="workflows-drawer-body">
            {/* Config-driven metrics – for ALL pipeline types (Glue, Step Functions, EMR, Airflow) */}
            {flattenMetricsForDisplay(metricsForPipeline).length > 0 && (
              <div className="workflows-drawer-card workflows-drawer-card--metrics">
                <div className="workflows-drawer-section__title">Metrics</div>
                {configMetricsLoading ? (
                  <div className="workflows-drawer-loading"><Spin size="small" /></div>
                ) : (
                  <div className="workflows-drawer-metrics-grid">
                    {flattenMetricsForDisplay(metricsForPipeline).map((m, i) => {
                      const metricName = m._metricName ?? m.name;
                      const key = m.SK || m.metric_config_id || `${m.namespace}#${metricName}` || `m${i}`;
                      const val = configMetricValues[key];
                      const displayVal = val != null ? (Number.isInteger(val) ? val : val.toFixed(2)) : "—";
                      const label = metricName || (m.namespace && metricName ? `${m.namespace} / ${metricName}` : null) || m.namespace || "Metric";
                      return (
                        <div key={key} className="workflows-drawer-metric-card">
                          <span className="workflows-drawer-metric-card__value">{displayVal}</span>
                          <span className="workflows-drawer-metric-card__label">{String(label).slice(0, 24)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Phase 2: Airflow DAG status (health, last run, run history, trigger) – no metrics grid */}
            {isAirflowNode && (
              <div className="workflows-drawer-card workflows-drawer-card--metrics">
                {dagPhase2Loading ? (
                  <div className="workflows-drawer-loading"><Spin size="small" /></div>
                ) : (
                  <>
                    <div className="workflows-drawer-status-row">
                      {dagMetrics?.health_score != null && (
                        <div className="workflows-drawer-health">
                          <Progress
                            type="circle"
                            percent={dagMetrics.health_score}
                            size={56}
                            strokeColor={dagMetrics.health_score >= 80 ? "#52c41a" : dagMetrics.health_score >= 50 ? "#faad14" : "#ff4d4f"}
                          />
                          <span className="workflows-drawer-health__label">Health</span>
                        </div>
                      )}
                      {dagDetail?.last_run && (
                        <div className="workflows-drawer-last-run">
                          <span className={`workflows-drawer-last-run__dot workflows-drawer-last-run__dot--${dagDetail.last_run.state}`} />
                          <span>Last run {dagDetail.last_run.state}</span>
                          <span className="workflows-drawer-last-run__time">{formatTimeAgo(dagDetail.last_run.end_date || dagDetail.last_run.start_date)}</span>
                        </div>
                      )}
                    </div>
                    {dagMetrics?.duration_timeseries?.length > 0 && (
                      <div className="workflows-drawer-chart">
                        <div className="workflows-drawer-section__title">Run duration – last 7 days</div>
                        <div style={{ height: 100 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={dagMetrics.duration_timeseries.map((p) => ({ ...p, date: p.timestamp?.slice(0, 10) }))}>
                              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 10 }} width={32} />
                              <Tooltip formatter={(v) => [formatDuration(v), "Duration"]} />
                              <Line type="monotone" dataKey="duration_seconds" stroke="var(--wf-accent)" strokeWidth={2} dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                    {dagRuns?.runs?.length > 0 && (
                      <Collapse ghost size="small" className="workflows-drawer-collapse" items={[{
                        key: "runs",
                        label: <span className="workflows-drawer-collapse__label">Run history ({dagRuns.total ?? dagRuns.runs.length})</span>,
                        children: (
                          <div className="workflows-drawer-runs-list">
                            {dagRuns.runs.slice(0, 8).map((r, i) => (
                              <div key={r.run_id || i} className="workflows-drawer-run-item">
                                <span className={`workflows-drawer-run-item__state workflows-drawer-run-item__state--${r.state}`}>{r.state}</span>
                                <span>{formatDuration(r.duration_seconds)}</span>
                                <span className="workflows-drawer-run-item__date">{r.logical_date?.slice(0, 16) || r.start_date?.slice(0, 16)}</span>
                              </div>
                            ))}
                          </div>
                        ),
                      }]} />
                    )}
                    {dagDetail && (
                      <Collapse ghost size="small" className="workflows-drawer-collapse" items={[{
                        key: "config",
                        label: <span className="workflows-drawer-collapse__label">Configuration</span>,
                        children: (
                          <div className="workflows-drawer-config">
                            {dagDetail.schedule_interval && <div><strong>Schedule:</strong> {dagDetail.schedule_interval}</div>}
                            {dagDetail.task_count != null && <div><strong>Tasks:</strong> {dagDetail.task_count}</div>}
                            {dagDetail.tags?.length > 0 && <div><strong>Tags:</strong> {dagDetail.tags.join(", ")}</div>}
                          </div>
                        ),
                      }]} />
                    )}
                    {isAirflowNode && (
                      <Button type="primary" icon={<PlayCircleOutlined />} loading={triggerLoading} onClick={handleTriggerRun} block className="workflows-drawer-btn workflows-drawer-btn--trigger">
                        Trigger Run
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Dependencies – card style */}
            <div className="workflows-drawer-card">
              <div className="workflows-drawer-section__title workflows-drawer-section__title--with-count">
                Dependencies
                {(upstreamIds.length + downstreamIds.length) > 0 && (
                  <Tag color="blue" style={{ marginLeft: 6 }}>{upstreamIds.length + downstreamIds.length}</Tag>
                )}
              </div>
              <div className="workflows-drawer-deps">
                <div className="workflows-drawer-dep-card workflows-drawer-dep-card--downstream">
                  <div className="workflows-drawer-dep-card__label">Downstream (affected if this fails)</div>
                  {downstreamIds.length === 0 ? (
                    <div className="workflows-drawer-dep-card__empty">None</div>
                  ) : (
                    <div className="workflows-drawer-dep-card__list">
                      {downstreamIds.map((id) => {
                        const n = nodes.find((x) => x.id === id);
                        return <div key={id} className="workflows-drawer-dep-card__item">{n?.data?.pipelineName || id}</div>;
                      })}
                    </div>
                  )}
                </div>
                <div className="workflows-drawer-dep-card workflows-drawer-dep-card--upstream">
                  <div className="workflows-drawer-dep-card__label">Upstream (possible causes)</div>
                  {upstreamIds.length === 0 ? (
                    <div className="workflows-drawer-dep-card__empty">None</div>
                  ) : (
                    <div className="workflows-drawer-dep-card__list">
                      {upstreamIds.map((id) => {
                        const n = nodes.find((x) => x.id === id);
                        return <div key={id} className="workflows-drawer-dep-card__item">{n?.data?.pipelineName || id}</div>;
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Lineage */}
            <div className="workflows-drawer-card workflows-drawer-card--lineage">
              <div className="workflows-drawer-lineage">
                <div>
                  <div className="workflows-drawer-section__title">Lineage</div>
                  <div className="workflows-drawer-lineage__desc">Enable lineage tracking for this pipeline</div>
                </div>
                <Switch checked={lineageEnabled} loading={lineageLoading} onChange={handleLineageToggle} />
              </div>
            </div>

            {/* Logs & Metrics */}
            <Collapse
              defaultActiveKey={["logs", "metrics"]}
              ghost
              className="workflows-drawer-collapse workflows-drawer-collapse--main"
              items={[
                {
                  key: "logs",
                  label: <span className="workflows-drawer-collapse__label">Logs</span>,
                  children: logsMetricsLoading ? (
                    <Spin size="small" />
                  ) : logsForPipeline.length === 0 ? (
                    <div className="workflows-drawer-empty">No logs configured for this pipeline</div>
                  ) : (
                    <div className="workflows-drawer-checkboxes">
                      <Checkbox.Group
                        value={selectedLogIds}
                        onChange={setSelectedLogIds}
                        options={logsForPipeline.map((l) => {
                          const value = l.SK || l.log_arn || l.s3_uri || l.log_config_id || JSON.stringify(l);
                          const label = l.log_group_name || l.s3_uri || (l.bucket_name && l.prefix_path ? `${l.bucket_name}/${l.prefix_path}` : l.bucket_name) || l.log_arn || (l.SK && l.SK.replace(/^LOG#/, "")) || "Log";
                          return { value, label: String(label).slice(0, 72) };
                        })}
                      />
                    </div>
                  ),
                },
                {
                  key: "metrics",
                  label: <span className="workflows-drawer-collapse__label">Metrics</span>,
                  children: logsMetricsLoading ? null : metricsForPipeline.length === 0 ? (
                    <div className="workflows-drawer-empty">No metrics configured for this pipeline</div>
                  ) : (
                    <div className="workflows-drawer-checkboxes">
                      <Checkbox.Group
                        value={selectedMetricIds}
                        onChange={setSelectedMetricIds}
                        options={flattenMetricsForDisplay(metricsForPipeline).map((m) => {
                          const metricName = m._metricName ?? m.name;
                          const value = m.SK || m.metric_config_id || (metricName && m.namespace ? `${m.namespace}#${metricName}` : null) || metricName || m.id || JSON.stringify(m);
                          const label = metricName || (m.namespace && metricName ? `${m.namespace} / ${metricName}` : null) || m.namespace || m.service_type || (m.SK && m.SK.replace(/^SERVICE#/, "")) || "Metric";
                          return { value, label: String(label).slice(0, 72) };
                        })}
                      />
                    </div>
                  ),
                },
              ]}
            />

            {/* Action buttons */}
            <div className="workflows-drawer-actions">
              <Button type="primary" block onClick={applyLogsMetricsToNode} icon={<LinkOutlined />} className="workflows-drawer-btn workflows-drawer-btn--primary">
                Apply to pipeline node
              </Button>
              <Button block onClick={handleViewLogs} icon={<FileTextOutlined />} className="workflows-drawer-btn">
                View Logs
              </Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default WorkflowsPage;
